import {
  CloudWarningOutlineIcon,
  CloudDisconnectOutlineIcon,
  InfoOutlineIcon,
} from "@tandemdiabetes/sugarmate-react-components"
import classNames from "classnames"
import dayjs from "dayjs"
import type {
  ComponentPropsWithoutRef,
  FC,
  Ref,
  SVGProps,
} from "react"
import {
  useMemo,
  forwardRef,
} from "react"

import styles from "./UserDataSynced.module.scss"

import { usePolyglot } from "src/contexts"
import {
  useThisMinute,
  useDisplayDate,
  useDisplayTime,
} from "src/hooks"
import { round } from "src/utils"

const polyglotPrefix = "components.user_data_synced."

const dateOptions = {
  lowercase: true,
}

export interface Props extends ComponentPropsWithoutRef<"div"> {
  syncedAt: string | null;
}

export const UserDataSynced = forwardRef<HTMLDivElement, Props>(
  function UserDataSynced(props: Props, propRef: Ref<HTMLDivElement>) {
    const {
      syncedAt,
      ...divProps
    } = props

    const polyglot = usePolyglot()
    const thisMinute = useThisMinute()
    const displayDate = useDisplayDate(dateOptions)
    const displayTime = useDisplayTime()

    const minutesAgo: number | null = useMemo(
      () => {
        if (syncedAt === null) {
          return null
        }

        return Math.max(
          0,
          dayjs(thisMinute).diff(
            syncedAt,
            "minutes",
            true,
          ),
        )
      },
      [
        thisMinute,
        syncedAt,
      ],
    )

    const recencyStyle = useMemo(
      () => {
        if (minutesAgo === null) {
          return styles.recencyStale
        }

        if (minutesAgo < 10) {
          return styles.recencyCurrent
        }

        if (minutesAgo < 15) {
          return styles.recencyRelevant
        }

        return styles.recencyStale
      },
      [minutesAgo],
    )

    const Icon: FC<SVGProps<SVGElement>> | null = useMemo(
      () => {
        if (minutesAgo === null) {
          return CloudDisconnectOutlineIcon
        }

        if (minutesAgo < 10) {
          return null
        }

        if (minutesAgo < 15) {
          return CloudWarningOutlineIcon
        }

        return CloudDisconnectOutlineIcon
      },
      [minutesAgo],
    )

    const textString = useMemo(
      () => {
        if (minutesAgo === null || syncedAt === null) {
          return polyglot.t(
            `${polyglotPrefix}no_data`,
          )
        }

        if (minutesAgo < 1) {
          return polyglot.t(`${polyglotPrefix}just_now`)
        }

        if (minutesAgo < 60) {
          return polyglot.t(
            `${polyglotPrefix}minutes_ago`,
            round(minutesAgo),
          )
        }

        const syncedToday = dayjs(syncedAt).isSame(
          thisMinute,
          "day",
        )

        if (syncedToday) {
          return polyglot.t(
            `${polyglotPrefix}at_time`,
            { time: displayTime(syncedAt) },
          )
        }

        return polyglot.t(
          `${polyglotPrefix}at_date_and_time`,
          {
            date: displayDate(syncedAt),
            time: displayTime(syncedAt),
          },
        )
      },
      [
        minutesAgo,
        thisMinute,
        syncedAt,
        polyglot,
        displayDate,
        displayTime,
      ],
    )

    return (
      <div
        {...divProps}
        className={classNames(
          divProps.className,
          styles.container,
        )}
        ref={propRef}
      >
        {Icon && (
          <Icon
            className={classNames(
              styles.syncIcon,
              recencyStyle,
            )}
          />
        )}
        <div
          className={classNames(
            styles.text,
            recencyStyle,
          )}
          id="cgm-reading-time"
        >
          {textString}
        </div>
        {/* TODO this will be an info button */}
        <InfoOutlineIcon className={styles.infoIcon} />
      </div>
    )
  },
)
